import { memo } from "react";

import "./mobile-transaction-card.css";

/**
 * MobileTransactionCard
 * ------------------------------------------------------------------
 * Generic, presentation-only card for transaction lists on MOBILE view.
 * Reusable by any module — the module supplies its own nodes and fields,
 * so this component has zero module-specific logic.
 *
 * Props:
 * - title        : node   → e.g. <ReferenceNoColumn /> (keeps existing link style + copy icon)
 * - badge        : node   → e.g. <Badge color={...} value={...} />
 * - actions      : node   → e.g. <StaticDropDownActions /> (not affected by card onClick)
 * - subtitle     : string → line below the title (e.g. Fund Cluster)
 * - fields       : array  → [{ label, value, isAmount, fullWidth }] rendered as a 2-column grid
 *                           (fullWidth: true makes the field span the whole row — for long values like remarks)
 * - footerDate   : string → date shown in the footer (formatted as "16 Jun 2026")
 * - footerBy     : string → user shown beside the date
 * - onClick      : func   → fired when the card body is tapped (title/actions keep their own handlers)
 */

const formatAmount = (value) => {
  const number = Number(value);
  if (value === null || value === undefined || value === "" || Number.isNaN(number)) return "0.00";
  return number.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const MobileTransactionCard = ({ title, badge, actions, subtitle, fields = [], footerDate, footerBy, onClick }) => (
  <div className="mobile-transaction-card">
    <div className="d-flex align-items-start justify-content-between gap-2">
      <div className="flex-grow-1 min-w-0">
        {title}
        {subtitle && (
          <div className="mobile-transaction-card__subtitle" onClick={onClick}>
            {subtitle}
          </div>
        )}
      </div>
      <div className="d-flex align-items-center gap-1 flex-shrink-0">
        {badge}
        {actions}
      </div>
    </div>

    {fields.length > 0 && (
      <div className="mobile-transaction-card__panel row g-0" onClick={onClick}>
        {fields.map(({ label, value, isAmount, fullWidth }) => (
          <div key={label} className={`${fullWidth ? "col-12" : "col-6"} mobile-transaction-card__field`}>
            <div className="mobile-transaction-card__label">{label}</div>
            <div className="mobile-transaction-card__value">{isAmount ? formatAmount(value) : value || "-"}</div>
          </div>
        ))}
      </div>
    )}

    {(footerDate || footerBy) && (
      <div className="mobile-transaction-card__footer" onClick={onClick}>
        <i className="ri-time-line align-bottom me-1" />
        {formatDate(footerDate)}
        {footerBy && <span> &middot; {footerBy}</span>}
      </div>
    )}
  </div>
);

export default memo(MobileTransactionCard);